import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Mic, Mail, Type, Zap, AppWindow, KeyRound } from "lucide-react";

const features = [
  {
    icon: AppWindow,
    title: "Open Any App",
    desc: "Say 'open Slack' or 'open Teams' and ClawdVoice launches it instantly. No hunting through taskbars.",
    span: "col-span-1 md:col-span-2",
    large: true,
  },
  {
    icon: Mail,
    title: "Draft Emails",
    desc: "Dictate your email naturally. ClawdVoice opens Gmail and pre-fills your message — ready to send.",
    span: "col-span-1",
    large: false,
  },
  {
    icon: Type,
    title: "Speech to Text",
    desc: "Transcribe into any input field. Speak your thoughts and watch them appear.",
    span: "col-span-1",
    large: false,
  },
  {
    icon: Zap,
    title: "Zero Interruption",
    desc: "No pop-ups. No UI. Just results. ClawdVoice works silently in the background.",
    span: "col-span-1",
    large: false,
  },
  {
    icon: KeyRound,
    title: "One Shortcut",
    desc: "One key to remember: FN. Hold it, speak, release. That's the entire UX.",
    span: "col-span-1 md:col-span-2",
    large: true,
  },
  {
    icon: Mic,
    title: "Mac & Windows",
    desc: "Native performance on both platforms. ClawdVoice feels like part of the OS.",
    span: "col-span-1",
    large: false,
  },
];

function FeatureCard({ feature, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  const Icon = feature.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      data-testid={`feature-card-${index}`}
      className={`${feature.span} bg-white border border-zinc-200 p-8 rounded-md hover:border-zinc-300 hover:shadow-sm transition-all group`}
    >
      <div className="flex flex-col h-full gap-4">
        <div className="w-10 h-10 bg-zinc-100 rounded-sm flex items-center justify-center group-hover:bg-zinc-900 transition-colors">
          <Icon
            size={20}
            strokeWidth={1.5}
            className="text-zinc-600 group-hover:text-white transition-colors"
          />
        </div>
        <div>
          <h3
            style={{ fontFamily: "Manrope, sans-serif" }}
            className={`font-semibold text-zinc-900 mb-2 ${feature.large ? "text-2xl" : "text-lg"}`}
          >
            {feature.title}
          </h3>
          <p className="text-zinc-500 text-sm leading-relaxed">{feature.desc}</p>
        </div>
        {feature.large && (
          <div className="mt-auto">
            <span className="fn-key text-xs px-2 py-1 inline-flex">FN</span>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default function Features() {
  const titleRef = useRef(null);
  const titleInView = useInView(titleRef, { once: true });

  return (
    <section
      id="features"
      data-testid="features-section"
      className="py-32 bg-[#FAFAFA]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div ref={titleRef}>
          <motion.p
            initial={{ opacity: 0 }}
            animate={titleInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.4 }}
            className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-4"
          >
            Capabilities
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            animate={titleInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            style={{ fontFamily: "Manrope, sans-serif" }}
            className="text-3xl md:text-5xl font-semibold tracking-tight text-zinc-900 mb-16 max-w-2xl"
          >
            Everything you need.
            <br />
            Nothing you don't.
          </motion.h2>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {features.map((f, i) => (
            <FeatureCard key={i} feature={f} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
